import React, {useContext, useEffect, useRef, useState} from 'react';
import {useDispatch} from "react-redux";
import api from "../../core/api/base-api";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material"
import {LoadingButton} from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import BoltIcon from "@mui/icons-material/Bolt";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ScheduleIcon from "@mui/icons-material/Schedule";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SyncIcon from "@mui/icons-material/Sync";
import {format} from "date-fns";
import Loading from "../../components/Common/Loading";
import {SnackBarContext} from "../../snackBarProvider";
import {
    useAddBossAllianceBoostTOHMutation,
    useAddBossAllianceDamageTOHMutation,
    useClearAllBossAllianceProgressTOHMutation,
    useClearAllClashProgressTOHMutation,
    useClearAllDepotProgressTOHMutation,
    useClearBossAllianceProgressTOHMutation,
    useClearClashProgressTOHMutation,
    useClearDepotProgressTOHMutation,
    useGetAroundBossAllianceEventsTOHQuery,
    useGetBossAllianceBoostsTOHQuery,
    useGetBossAllianceProgressTOHQuery,
    useAwardBossLeagueTOHMutation,
    useGetBossLeagueRecalculationStatusTOHQuery,
    useGetBossLeaguesTOHQuery,
    useGetBossLeagueTopTOHQuery,
    useScheduleBossLeagueRecalculationTOHMutation,
    useSetBossLeagueScoresTOHMutation,
} from "../../core/api/toh-clans-api";


export const formatDateTime = (value) => {
    if (!value) return '-';
    try {
        return format(new Date(value), "dd.MM.yyyy HH:mm:ss");
    } catch {
        return String(value);
    }
};


export const eventStatus = (event) => {
    const now = Date.now();
    const start = event?.distribution?.start ? new Date(event.distribution.start).getTime() : null;
    const end = event?.distribution?.end ? new Date(event.distribution.end).getTime() : null;
    if (start && now < start) return {label: 'Upcoming', color: 'info'};
    if (end && now > end) return {label: 'Ended', color: 'default'};
    return {label: 'Active', color: 'success'};
};


const toEventList = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};


export const flattenAroundEvents = ({active, next, previous} = {}) => {
    const list = [];
    const seen = new Set();
    for (const chunk of [previous, active, next]) {
        for (const event of toEventList(chunk)) {
            if (event?.id && !seen.has(event.id)) {
                seen.add(event.id);
                list.push(event);
            }
        }
    }
    return list;
};


const formatLastPointsAt = (value) => {
    if (!value) return '-';
    try {
        const date = new Date(value);
        if (date.getTime() <= 0 || date.getUTCFullYear() <= 1970) return '-';
        return formatDateTime(value);
    } catch {
        return String(value);
    }
};


const formatPosition = (position) => {
    if (position == null || position < 0) return 'Unranked';
    return String(position);
};


export function ClanLeagueProgressCard({
    eventId,
    defaultClanId,
    pointsLabel,
    useProgressQuery,
    extraScoreFields = [],
}) {
    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);

    useEffect(() => {
        if (!clanIdTouched) setClanId(defaultClanId || '');
    }, [defaultClanId, clanIdTouched]);

    const trimmedClanId = clanId.trim();
    const skip = !eventId || !trimmedClanId;

    const {
        data: progress,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useProgressQuery({eventId, clanId: trimmedClanId}, {skip});

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <EmojiEventsIcon color="warning"/>
                        <Typography variant="h6">Clan League Progress</Typography>
                    </Stack>
                    {!skip && (
                        <Tooltip title="Reload progress">
                            <span>
                                <IconButton size="small" onClick={refetch} disabled={isFetching}>
                                    <RefreshIcon/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start', mb: 2}}>
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth
                        value={clanId}
                        onChange={(e) => { setClanId(e.target.value); setClanIdTouched(true); }}
                        helperText={
                            defaultClanId
                                ? "Auto-filled from current player's clan; override to view another clan"
                                : "Enter a clan ID to view league progress"
                        }
                    />
                    {clanIdTouched && defaultClanId && (
                        <Tooltip title="Reset to player's clan">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => { setClanId(defaultClanId); setClanIdTouched(false); }}
                                >
                                    Reset
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                {!eventId ? (
                    <Alert severity="info">
                        Select an event above to view progress
                    </Alert>
                ) : !trimmedClanId ? (
                    <Alert severity="info">
                        Enter a Clan ID to view progress
                    </Alert>
                ) : (
                    <>
                        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block', mb: 1}}>
                            Event <Box component="span">{eventId}</Box>
                            {' · '}
                            Clan <Box component="span">{trimmedClanId}</Box>
                        </Typography>

                        <Loading isLoading={isLoading} isError={isError} error={error}>
                            {progress ? (
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary', width: '40%'}}>Clan</TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{progress.clanName || '-'}</Typography>
                                                <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                                    {progress.clanId || trimmedClanId}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>League</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                                    <Chip label={progress.league ?? '-'} size="small" color="warning"/>
                                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                                        prev: {progress.previousLeague ?? '-'}
                                                    </Typography>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Position</TableCell>
                                            <TableCell>{formatPosition(progress.position)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Promotion / Demotion zone</TableCell>
                                            <TableCell>
                                                {progress.promotionZone ?? '-'} / {progress.demotionZone ?? '-'}
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>{pointsLabel}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                                    {progress.points ?? 0}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                        {extraScoreFields.map(({field, label}) => (
                                            <TableRow key={field}>
                                                <TableCell sx={{color: 'text.secondary'}}>{label}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{fontWeight: 600}}>
                                                        {progress[field] ?? 0}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Last points at</TableCell>
                                            <TableCell>{formatLastPointsAt(progress.lastPointsAt)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography sx={{color: 'text.secondary', py: 1}}>
                                    No progress for this clan in this event
                                </Typography>
                            )}
                        </Loading>
                    </>
                )}
            </CardContent>
        </Card>
    );
}


function buildDraftScores(top, scoreFields) {
    const draft = {};
    if (!Array.isArray(top)) return draft;
    top.forEach((entry) => {
        if (!entry?.clanId) return;
        draft[entry.clanId] = Object.fromEntries(
            scoreFields.map(({key}) => [key, String(entry[key] ?? 0)]),
        );
    });
    return draft;
}

function parseDraftScore(value) {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function getChangedScores(top, draftScores, scoreFields) {
    if (!Array.isArray(top)) return [];
    const scores = [];
    for (const entry of top) {
        if (!entry?.clanId) continue;
        const draft = draftScores[entry.clanId];
        if (!draft) continue;
        const parsedValues = scoreFields.map(({key}) => parseDraftScore(draft[key]));
        if (parsedValues.some((value) => value == null)) continue;
        const changed = scoreFields.some(
            ({key}, index) => parsedValues[index] !== Number(entry[key] ?? 0),
        );
        if (!changed) continue;
        const score = {clanId: entry.clanId};
        scoreFields.forEach(({requestKey}, index) => {
            score[requestKey] = parsedValues[index];
        });
        scores.push(score);
    }
    return scores;
}

function hasInvalidDraftScores(top, draftScores, scoreFields) {
    if (!Array.isArray(top)) return false;
    return top.some((entry) => {
        if (!entry?.clanId) return false;
        const draft = draftScores[entry.clanId];
        if (!draft) return false;
        return scoreFields.some(({key}) => parseDraftScore(draft[key]) == null);
    });
}


function LeagueTopDialog({
    target,
    pointsLabel,
    useLeagueTopQuery,
    useAwardMutation,
    useSetScoresMutation,
    scoreFields,
    onClose,
}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const open = Boolean(target);
    const eventId = target?.eventId;
    const league = target?.league;
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [editing, setEditing] = useState(false);
    const [draftScores, setDraftScores] = useState({});
    const effectiveScoreFields = scoreFields?.length
        ? scoreFields
        : [{key: 'points', label: pointsLabel, requestKey: 'score'}];

    const {
        data: top,
        isLoading,
        isError,
        error,
        refetch,
    } = useLeagueTopQuery(
        {eventId, league},
        {skip: !open || eventId == null || league == null},
    );

    const [award, awardResponse] = useAwardMutation();
    const [setScores, setScoresResponse] = useSetScoresMutation();

    useEffect(() => {
        if (!open) {
            setEditing(false);
            setDraftScores({});
            setConfirmOpen(false);
            setSaveConfirmOpen(false);
        }
    }, [open]);

    const changedScores = getChangedScores(top, draftScores, effectiveScoreFields);
    const invalidDraft = hasInvalidDraftScores(top, draftScores, effectiveScoreFields);
    const canSave = editing && changedScores.length > 0 && !invalidDraft;

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            addAlert({text: 'Copied to clipboard', type: 'success'});
        } catch {
            addAlert({text: 'Failed to copy', type: 'error'});
        }
    };

    const handleAward = async () => {
        const response = await award({eventId, league});
        setConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to award scores';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            addAlert({text: `Scores awarded for league ${league}`, type: 'success'});
            refetch();
        }
    };

    const handleEnterEdit = () => {
        setDraftScores(buildDraftScores(top, effectiveScoreFields));
        setEditing(true);
    };

    const handleCancelEdit = () => {
        setEditing(false);
        setDraftScores({});
        setSaveConfirmOpen(false);
    };

    const handleSave = async () => {
        const scores = getChangedScores(top, draftScores, effectiveScoreFields);
        const response = await setScores({eventId, league, scores});
        setSaveConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to set scores';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            addAlert({text: `Updated scores for ${scores.length} clan(s) in league ${league}`, type: 'success'});
            setEditing(false);
            setDraftScores({});
            refetch();
        }
    };

    const handleClose = () => {
        setEditing(false);
        setDraftScores({});
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth sx={{zIndex: 10001}}>
                <DialogTitle>
                    <Typography variant="h6" gutterBottom>
                        League {league ?? '-'} Top
                    </Typography>
                    {eventId && (
                        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block', mb: 2}}>
                            Event {eventId}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Loading isLoading={isLoading} isError={isError} error={error}>
                        {Array.isArray(top) && top.length > 0 ? (
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell width={56}>#</TableCell>
                                        <TableCell>Clan</TableCell>
                                        {effectiveScoreFields.map(({key, label}) => (
                                            <TableCell key={key} align="right">{label}</TableCell>
                                        ))}
                                        <TableCell align="right" width={48}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {top.map((entry, idx) => {
                                        const clanId = entry.clanId;
                                        return (
                                            <TableRow key={clanId || idx}>
                                                <TableCell>{idx + 1}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{entry.clanName || '-'}</Typography>
                                                    <Typography variant="caption" sx={{color: 'text.secondary'}}>
                                                        {clanId || '-'}
                                                    </Typography>
                                                </TableCell>
                                                {effectiveScoreFields.map(({key}) => {
                                                    const draftValue = clanId != null
                                                        ? (draftScores[clanId]?.[key] ?? String(entry[key] ?? 0))
                                                        : '';
                                                    const isInvalid = editing && clanId
                                                        && parseDraftScore(draftValue) == null;
                                                    return (
                                                        <TableCell key={key} align="right">
                                                            {editing && clanId ? (
                                                                <TextField
                                                                    size="small"
                                                                    type="number"
                                                                    value={draftValue}
                                                                    onChange={(e) => setDraftScores((prev) => ({
                                                                        ...prev,
                                                                        [clanId]: {
                                                                            ...prev[clanId],
                                                                            [key]: e.target.value,
                                                                        },
                                                                    }))}
                                                                    inputProps={{min: 0, step: 1, style: {textAlign: 'right'}}}
                                                                    error={isInvalid}
                                                                    sx={{width: 140}}
                                                                />
                                                            ) : (
                                                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                                                    {entry[key] ?? 0}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                    );
                                                })}
                                                <TableCell align="right">
                                                    {clanId && (
                                                        <Tooltip title="Copy clan ID">
                                                            <IconButton size="small" onClick={() => handleCopy(clanId)}>
                                                                <ContentCopyIcon fontSize="small"/>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        ) : (
                            <Typography sx={{color: 'text.secondary', py: 1}}>
                                No clans in this league top
                            </Typography>
                        )}
                    </Loading>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                    {editing ? (
                        <>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                            <LoadingButton
                                loading={setScoresResponse.isLoading}
                                variant="contained"
                                color="primary"
                                disabled={!canSave}
                                onClick={() => setSaveConfirmOpen(true)}
                            >
                                Save ({changedScores.length})
                            </LoadingButton>
                        </>
                    ) : (
                        <>
                            <Button
                                variant="outlined"
                                disabled={!Array.isArray(top) || top.length === 0}
                                onClick={handleEnterEdit}
                            >
                                Edit
                            </Button>
                            <LoadingButton
                                loading={awardResponse.isLoading}
                                variant="contained"
                                color="warning"
                                disabled={eventId == null || league == null}
                                onClick={() => setConfirmOpen(true)}
                            >
                                Award scores
                            </LoadingButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} sx={{zIndex: 10002}}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm Award Scores</Typography>
                    <Alert severity="warning" sx={{mb: 2}}>
                        Awards random scores to clans in this league. This cannot be undone.
                    </Alert>
                    <Typography>League <strong>{league ?? '-'}</strong></Typography>
                    <Typography>Event <strong>{eventId || '-'}</strong></Typography>
                </DialogContent>
                <DialogActions>
                    <Button  onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={awardResponse.isLoading}
                        variant="contained"
                        color="warning"
                        onClick={handleAward}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <Dialog open={saveConfirmOpen} onClose={() => setSaveConfirmOpen(false)} sx={{zIndex: 10002}}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm Set Scores</Typography>
                    <Alert severity="warning" sx={{mb: 2}}>
                        Sets absolute scores for {changedScores.length} clan(s). Clans without progress in this league are skipped.
                    </Alert>
                    <Typography>League <strong>{league ?? '-'}</strong></Typography>
                    <Typography>Event <strong>{eventId || '-'}</strong></Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSaveConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={setScoresResponse.isLoading}
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}


export function LeagueOverviewCard({
    eventId,
    pointsLabel,
    useLeaguesQuery,
    useLeagueTopQuery,
    useAwardMutation,
    useSetScoresMutation,
    scoreFields,
}) {
    const [topTarget, setTopTarget] = useState(null);
    const skip = !eventId;

    const {
        data: leagues,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useLeaguesQuery({eventId}, {skip});

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <EmojiEventsIcon color="primary"/>
                        <Typography variant="h6">League Overview</Typography>
                    </Stack>
                    {!skip && (
                        <Tooltip title="Reload leagues">
                            <span>
                                <IconButton size="small" onClick={refetch} disabled={isFetching}>
                                    <RefreshIcon/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                {!eventId ? (
                    <Alert severity="info">
                        Select an event above to view leagues
                    </Alert>
                ) : (
                    <>
                        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block', mb: 1}}>
                            Event <Box component="span">{eventId}</Box>
                        </Typography>

                        <Loading isLoading={isLoading} isError={isError} error={error}>
                            {Array.isArray(leagues) && leagues.length > 0 ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>League</TableCell>
                                            <TableCell align="right">Clans</TableCell>
                                            <TableCell align="right">Capacity</TableCell>
                                            <TableCell align="right"></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {leagues.map((row) => (
                                            <TableRow key={row.league} hover>
                                                <TableCell>
                                                    <Chip
                                                        label={row.league ?? '-'}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell align="right">{row.clansCount ?? 0}</TableCell>
                                                <TableCell align="right">
                                                    {row.capacity == null ? '—' : row.capacity}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => setTopTarget({eventId, league: row.league})}
                                                    >
                                                        Top
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography sx={{color: 'text.secondary', py: 1}}>
                                    No leagues
                                </Typography>
                            )}
                        </Loading>
                    </>
                )}
            </CardContent>

            <LeagueTopDialog
                target={topTarget}
                pointsLabel={pointsLabel}
                useLeagueTopQuery={useLeagueTopQuery}
                useAwardMutation={useAwardMutation}
                useSetScoresMutation={useSetScoresMutation}
                scoreFields={scoreFields}
                onClose={() => setTopTarget(null)}
            />
        </Card>
    );
}


const RECALC_STATUS_COLOR = {
    NOT_SCHEDULED: 'default',
    SCHEDULED: 'info',
    RUNNING: 'warning',
    SUCCEEDED: 'success',
    FAILED: 'error',
};

const RECALC_IN_PROGRESS = new Set(['SCHEDULED', 'RUNNING']);


export function LeagueRecalculationCard({
    events = [],
    defaultTargetEventId = '',
    useStatusQuery,
    useScheduleMutation,
    invalidateTags = [],
}) {
    const dispatch = useDispatch();
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();
    const prevStatusRef = useRef(null);

    const [targetEventId, setTargetEventId] = useState(defaultTargetEventId || '');
    const [sourceEventId, setSourceEventId] = useState('');
    const [runId, setRunId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pollingInterval, setPollingInterval] = useState(0);

    useEffect(() => {
        if (defaultTargetEventId) {
            setTargetEventId((prev) => prev || defaultTargetEventId);
        }
    }, [defaultTargetEventId]);

    useEffect(() => {
        setRunId(null);
        prevStatusRef.current = null;
    }, [targetEventId]);

    const [schedule, scheduleResponse] = useScheduleMutation();

    const trimmedTargetEventId = targetEventId.trim();
    const trimmedSourceEventId = sourceEventId.trim();

    const {
        data: status,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useStatusQuery(
        {eventId: trimmedTargetEventId, runId},
        {
            skip: !trimmedTargetEventId || !runId,
            pollingInterval,
        },
    );

    useEffect(() => {
        setPollingInterval(RECALC_IN_PROGRESS.has(status?.status) ? 2000 : 0);
    }, [status?.status]);

    useEffect(() => {
        const prev = prevStatusRef.current;
        const next = status?.status ?? null;
        prevStatusRef.current = next;

        if (
            RECALC_IN_PROGRESS.has(prev)
            && next
            && !RECALC_IN_PROGRESS.has(next)
            && invalidateTags.length > 0
        ) {
            dispatch(api.util.invalidateTags(invalidateTags));
        }
    }, [status?.status, dispatch, invalidateTags]);

    const inProgress = RECALC_IN_PROGRESS.has(status?.status);
    const canStart = Boolean(trimmedTargetEventId) && !inProgress && !scheduleResponse.isLoading;

    const handleStart = async () => {
        const body = {eventId: trimmedTargetEventId};
        if (trimmedSourceEventId) body.prevEventId = trimmedSourceEventId;
        const response = await schedule(body);
        setConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to schedule recalculation';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            const nextRunId = response.data?.runId || null;
            setRunId(nextRunId);
            addAlert({text: `League recalculation scheduled for ${trimmedTargetEventId}`, type: 'success'});
            refetch();
        }
    };

    const eventIds = events.map((e) => e.id).filter(Boolean);
    const sourceOptions = eventIds.filter((id) => id !== trimmedTargetEventId);

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <SyncIcon />
                        <Typography variant="h6">League Recalculation Process</Typography>
                    </Stack>
                    {trimmedTargetEventId && (
                        <Tooltip title="Reload status">
                            <span>
                                <IconButton size="small" onClick={refetch} disabled={isFetching}>
                                    <RefreshIcon/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                <Typography variant="body2" sx={{color: 'text.secondary', mb: 2}}>
                    Starts an async league recalculation for the target event.
                    Source event is optional. Run ID is kept only in this session.
                </Typography>

                <Stack spacing={2} sx={{mb: 2}}>
                    <Autocomplete
                        freeSolo
                        size="small"
                        options={eventIds}
                        value={targetEventId}
                        onChange={(_, value) => setTargetEventId(value || '')}
                        onInputChange={(_, value) => setTargetEventId(value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label="Target event" required/>
                        )}
                    />

                    <Autocomplete
                        freeSolo
                        size="small"
                        options={sourceOptions}
                        value={sourceEventId}
                        onChange={(_, value) => setSourceEventId(value || '')}
                        onInputChange={(_, value) => setSourceEventId(value || '')}
                        renderInput={(params) => (
                            <TextField {...params} label="Source event (optional)"/>
                        )}
                    />

                    <Box>
                        <LoadingButton
                            loading={scheduleResponse.isLoading}
                            variant="contained"
                            
                            startIcon={<SyncIcon/>}
                            disabled={!canStart}
                            onClick={() => setConfirmOpen(true)}
                        >
                            Start Recalculation
                        </LoadingButton>
                    </Box>
                </Stack>

                {!trimmedTargetEventId ? (
                    <Alert severity="info">Select or enter a target event to view status</Alert>
                ) : (
                    <Loading isLoading={isLoading} isError={isError} error={error}>
                        {status ? (
                            <>
                                {status.status === 'FAILED' && status.errorMessage && (
                                    <Alert severity="error" sx={{mb: 2}}>{status.errorMessage}</Alert>
                                )}
                                <Table size="small">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary', width: '40%'}}>Status</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={status.status || 'UNKNOWN'}
                                                    size="small"
                                                    color={RECALC_STATUS_COLOR[status.status] || 'default'}
                                                    variant={status.status === 'RUNNING' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Flags</TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={0.5} sx={{flexWrap: 'wrap', gap: 0.5}}>
                                                    <Chip
                                                        label="scheduled"
                                                        size="small"
                                                        color={status.scheduled ? 'info' : 'default'}
                                                        variant={status.scheduled ? 'filled' : 'outlined'}
                                                    />
                                                    <Chip
                                                        label="running"
                                                        size="small"
                                                        color={status.running ? 'warning' : 'default'}
                                                        variant={status.running ? 'filled' : 'outlined'}
                                                    />
                                                    <Chip
                                                        label="prepared"
                                                        size="small"
                                                        color={status.prepared ? 'success' : 'default'}
                                                        variant={status.prepared ? 'filled' : 'outlined'}
                                                    />
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Run ID</TableCell>
                                            <TableCell>{status.runId || runId || '—'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Event</TableCell>
                                            <TableCell>{status.eventId || trimmedTargetEventId}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Previous event</TableCell>
                                            <TableCell>{status.previousEventId || '—'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Category</TableCell>
                                            <TableCell>{status.eventCategory || '—'}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Created</TableCell>
                                            <TableCell>{formatDateTime(status.createdAt)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Started</TableCell>
                                            <TableCell>{formatDateTime(status.startedAt)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Completed</TableCell>
                                            <TableCell>{formatDateTime(status.completedAt)}</TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell sx={{color: 'text.secondary'}}>Updated</TableCell>
                                            <TableCell>{formatDateTime(status.updatedAt)}</TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </>
                        ) : (
                            <Typography sx={{color: 'text.secondary', py: 1}}>
                                No status data
                            </Typography>
                        )}
                    </Loading>
                )}
            </CardContent>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm League Recalculation</Typography>
                    <Alert severity="warning" sx={{mb: 2}}>
                        This starts an async process. It cannot be cancelled from Hermes.
                    </Alert>
                    <Typography>Target event</Typography>
                    <Typography sx={{mb: 1}}>{trimmedTargetEventId}</Typography>
                    <Typography>Source event</Typography>
                    <Typography>{trimmedSourceEventId || '—'}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button  onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={scheduleResponse.isLoading}
                        variant="contained"
                        
                        onClick={handleStart}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}


export const CLEAR_ACTIONS = {
    depot: {
        title: 'Clear Depot Progress',
        description: 'Deletes depot progress and donation history for the selected clan and event.',
        tooltip: 'Clear depot progress and donation history for a clan in this event',
        scope: 'clan',
    },
    boss: {
        title: 'Clear Boss Alliance Progress',
        description: 'Deletes clan progress, battle history, boosts and the active lock for the selected clan and event.',
        tooltip: 'Clear clan progress, battle history, boosts and lock in this event',
        scope: 'clan',
    },
    clash: {
        title: 'Clear Clash Progress',
        description: 'Deletes Clash league progress and finalized result for the selected clan and campaign.',
        tooltip: 'Clear Clash progress and finalized result for a clan in this campaign',
        scope: 'clan',
    },
    depotAll: {
        title: 'Clear All Depot Progress',
        description: 'Deletes ALL depot progress and donation history for every clan in this event.',
        tooltip: 'Clear entire event progress (all clans)',
        scope: 'all',
    },
    bossAll: {
        title: 'Clear All Boss Alliance Progress',
        description: 'Deletes ALL clan progress, battle history, boosts and locks for this event.',
        tooltip: 'Clear entire event progress (all clans)',
        scope: 'all',
    },
    clashAll: {
        title: 'Clear All Clash Progress',
        description: 'Deletes ALL Clash league progress and finalized results for every clan in this campaign.',
        tooltip: 'Clear entire Clash campaign progress (all clans)',
        scope: 'all',
    },
};


export function ClearProgressDialog({target, defaultClanId, onClose}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [clearDepotProgress, clearDepotResponse] = useClearDepotProgressTOHMutation();
    const [clearBossProgress, clearBossResponse] = useClearBossAllianceProgressTOHMutation();
    const [clearClashProgress, clearClashResponse] = useClearClashProgressTOHMutation();
    const [clearAllDepotProgress, clearAllDepotResponse] = useClearAllDepotProgressTOHMutation();
    const [clearAllBossProgress, clearAllBossResponse] = useClearAllBossAllianceProgressTOHMutation();
    const [clearAllClashProgress, clearAllClashResponse] = useClearAllClashProgressTOHMutation();

    const [clanId, setClanId] = useState(defaultClanId || '');

    useEffect(() => {
        if (target) setClanId(defaultClanId || '');
    }, [target, defaultClanId]);

    if (!target) return null;

    const action = CLEAR_ACTIONS[target.type];
    const isAll = action.scope === 'all';
    const isLoading = (
        clearDepotResponse.isLoading
        || clearBossResponse.isLoading
        || clearClashResponse.isLoading
        || clearAllDepotResponse.isLoading
        || clearAllBossResponse.isLoading
        || clearAllClashResponse.isLoading
    );
    const trimmedClanId = clanId.trim();
    const canConfirm = isAll ? Boolean(target.eventId) : Boolean(trimmedClanId);

    const handleSubmit = async () => {
        let response;
        if (target.type === 'depotAll') {
            response = await clearAllDepotProgress({eventId: target.eventId});
        } else if (target.type === 'bossAll') {
            response = await clearAllBossProgress({eventId: target.eventId});
        } else if (target.type === 'clashAll') {
            response = await clearAllClashProgress({eventId: target.eventId});
        } else if (target.type === 'depot') {
            response = await clearDepotProgress({eventId: target.eventId, clanId: trimmedClanId});
        } else if (target.type === 'clash') {
            response = await clearClashProgress({eventId: target.eventId, clanId: trimmedClanId});
        } else {
            response = await clearBossProgress({eventId: target.eventId, clanId: trimmedClanId});
        }
        onClose();
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || `Failed to ${action.title.toLowerCase()}`;
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            addAlert({
                text: isAll
                    ? `${action.title}: done for event ${target.eventId}`
                    : `${action.title}: done for clan ${trimmedClanId}`,
                type: 'success',
            });
        }
    };

    return (
        <Dialog open onClose={isLoading ? undefined : onClose}>
            <DialogContent>
                <Typography variant="h6" gutterBottom>Confirm {action.title}</Typography>
                <Alert severity={isAll ? 'error' : 'warning'} sx={{mb: 2}}>
                    {action.description} This cannot be undone.
                </Alert>
                <TextField
                    label="Event ID" size="small" fullWidth required
                    value={target.eventId}
                    InputProps={{readOnly: true}}
                    sx={{mt: 1}}
                />
                {!isAll && (
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth required
                        value={clanId}
                        onChange={(e) => setClanId(e.target.value)}
                        helperText="Auto-filled from player's clan; override if needed"
                        sx={{mt: 2}}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button  onClick={onClose}>Cancel</Button>
                <LoadingButton
                    loading={isLoading}
                    variant="contained"
                    color={isAll ? 'error' : 'warning'}
                    disabled={!canConfirm}
                    onClick={handleSubmit}
                >
                    Confirm
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}


function ActiveEventsCard({events, isLoading, isError, error, onRefresh, isFetching, onSelectEvent, selectedEventId, defaultClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [clearTarget, setClearTarget] = useState(null);

    const handleCopy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            addAlert({text: 'Copied to clipboard', type: 'success'});
        } catch {
            addAlert({text: 'Failed to copy', type: 'error'});
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Typography variant="h6">Boss Alliance Events</Typography>
                    <Tooltip title="Reload events">
                        <span>
                            <IconButton size="small" onClick={onRefresh} disabled={isFetching}>
                                <RefreshIcon/>
                            </IconButton>
                        </span>
                    </Tooltip>
                </Stack>

                <Loading isLoading={isLoading} isError={isError} error={error}>
                    {Array.isArray(events) && events.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Event ID</TableCell>
                                    <TableCell>Start</TableCell>
                                    <TableCell>End</TableCell>
                                    <TableCell>Created</TableCell>
                                    <TableCell align="right"></TableCell>
                                    <TableCell align="center">Boss Progress</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {events.map((event) => {
                                    const status = eventStatus(event);
                                    const isSelected = selectedEventId === event.id;
                                    return (
                                        <TableRow
                                            key={event.id}
                                            hover
                                            selected={isSelected}
                                            sx={{cursor: 'pointer'}}
                                            onClick={() => onSelectEvent(event.id)}
                                        >
                                            <TableCell>
                                                <Chip
                                                    icon={status.label === 'Active' ? <CheckCircleIcon/> : <ScheduleIcon/>}
                                                    label={status.label}
                                                    color={status.color}
                                                    size="small"
                                                    variant={status.label === 'Active' ? 'filled' : 'outlined'}
                                                />
                                            </TableCell>
                                            <TableCell sx={{}}>
                                                {event.id}
                                            </TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.start)}</TableCell>
                                            <TableCell>{formatDateTime(event.distribution?.end)}</TableCell>
                                            <TableCell>{formatDateTime(event.createdAt)}</TableCell>
                                            <TableCell align="right">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'flex-end'}}>
                                                    <Tooltip title="Copy event ID">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleCopy(event.id);
                                                            }}
                                                        >
                                                            <ContentCopyIcon fontSize="small"/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Button
                                                        size="small"
                                                        variant={isSelected ? "contained" : "outlined"}
                                                        color="error"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            onSelectEvent(event.id);
                                                        }}
                                                    >
                                                        {isSelected ? 'Selected' : 'Select'}
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} sx={{justifyContent: 'center'}}>
                                                    <Tooltip title={CLEAR_ACTIONS.boss.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'boss', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear
                                                        </Button>
                                                    </Tooltip>
                                                    <Tooltip title={CLEAR_ACTIONS.bossAll.tooltip}>
                                                        <Button
                                                            size="small"
                                                            variant="contained"
                                                            color="error"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setClearTarget({type: 'bossAll', eventId: event.id});
                                                            }}
                                                        >
                                                            Clear All
                                                        </Button>
                                                    </Tooltip>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <Typography sx={{color: 'text.secondary', py: 2}}>
                            No events
                        </Typography>
                    )}
                </Loading>
            </CardContent>

            <ClearProgressDialog
                target={clearTarget}
                defaultClanId={defaultClanId}
                onClose={() => setClearTarget(null)}
            />
        </Card>
    );
}


function AddDamageCard({eventId, deviceId, defaultClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [addDamage, addDamageResponse] = useAddBossAllianceDamageTOHMutation();

    const [userId, setUserId] = useState(deviceId || '');
    const [userIdTouched, setUserIdTouched] = useState(false);
    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);
    const [damage, setDamage] = useState('');
    const [reason, setReason] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (!userIdTouched) setUserId(deviceId || '');
    }, [deviceId, userIdTouched]);

    useEffect(() => {
        if (!clanIdTouched) setClanId(defaultClanId || '');
    }, [defaultClanId, clanIdTouched]);

    const damageNumber = parseInt(damage);
    const trimmedUserId = userId?.trim() ?? "";
    const isValid = (
        eventId?.trim()
        && clanId?.trim()
        && damage !== "" && Number.isFinite(damageNumber)
        && reason?.trim()
    );

    const handleSubmit = async () => {
        const body = {
            eventId: eventId.trim(),
            clanId: clanId.trim(),
            damage: damageNumber,
            reason: reason.trim(),
        };
        if (trimmedUserId) body.userId = trimmedUserId;
        const response = await addDamage(body);
        setConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to add damage';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            const target = trimmedUserId || 'clan leader';
            const signed = damageNumber > 0 ? `+${damageNumber}` : String(damageNumber);
            addAlert({text: `Damage ${signed} applied to ${target} (clan ${clanId})`, type: 'success'});
            setDamage('');
            setReason('');
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Add Damage</Typography>
                </Stack>
                <Typography variant="body2" sx={{color: 'text.secondary', mb: 2}}>
                    Manually adds damage to a clan, bypassing the battle flow (no anti-cheat checks).
                    If User ID is empty, damage is credited to the clan leader.
                    Negative values are allowed for corrections; clan total does not go below 0.
                </Typography>

                {!eventId && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        Select an event from the table above
                    </Alert>
                )}

                <Stack spacing={2}>
                    <TextField
                        label="Event ID" size="small" fullWidth required
                        value={eventId || ''}
                        InputProps={{readOnly: true}}
                        helperText="Set by clicking a row in the events table"
                    />
                    <TextField
                        label="User ID" size="small" fullWidth
                        value={userId}
                        onChange={(e) => { setUserId(e.target.value); setUserIdTouched(true); }}
                        helperText="Optional. Empty → credit clan leader. Auto-filled from current player"
                    />
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth required
                        value={clanId}
                        onChange={(e) => { setClanId(e.target.value); setClanIdTouched(true); }}
                        helperText="Auto-filled from player's clan; override if needed"
                    />
                    <TextField
                        label="Damage" size="small" fullWidth required type="number"
                        value={damage} onChange={(e) => setDamage(e.target.value)}
                        helperText="Can be negative (correction). Clan total is clamped at ≥ 0"
                    />
                    <TextField
                        label="Reason" size="small" fullWidth required multiline minRows={2}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        helperText="Goes to the audit log"
                    />
                </Stack>

                <Box sx={{mt: 2}}>
                    <LoadingButton
                        loading={addDamageResponse.isLoading}
                        variant="contained" color="primary"
                        startIcon={<WhatshotIcon/>}
                        disabled={!isValid}
                        onClick={() => setConfirmOpen(true)}
                    >
                        Add Damage
                    </LoadingButton>
                </Box>
            </CardContent>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm Add Damage</Typography>
                    <Typography>
                        Apply <strong>{damageNumber}</strong> damage to{' '}
                        <strong>{trimmedUserId || 'clan leader'}</strong>
                    </Typography>
                    <Typography>clan</Typography>
                    <Typography sx={{}}>{clanId}</Typography>
                    <Typography>on event</Typography>
                    <Typography sx={{}}>{eventId}</Typography>
                    <Typography sx={{mt: 1}}>Reason: <em>{reason}</em></Typography>
                </DialogContent>
                <DialogActions>
                    <Button  onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={addDamageResponse.isLoading}
                        variant="contained" color="error"
                        onClick={handleSubmit}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}


function AddBoostCard({eventId, deviceId, defaultClanId}) {
    const useSnackBars = () => useContext(SnackBarContext);
    const {addAlert} = useSnackBars();

    const [addBoost, addBoostResponse] = useAddBossAllianceBoostTOHMutation();

    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);
    const [boostId, setBoostId] = useState('');
    const [level, setLevel] = useState('1');
    const [buyerUserId, setBuyerUserId] = useState(deviceId || '');
    const [buyerTouched, setBuyerTouched] = useState(false);
    const [reason, setReason] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    useEffect(() => {
        if (!clanIdTouched) setClanId(defaultClanId || '');
    }, [defaultClanId, clanIdTouched]);

    useEffect(() => {
        if (!buyerTouched) setBuyerUserId(deviceId || '');
    }, [deviceId, buyerTouched]);

    const levelNumber = parseInt(level);
    const isValid = (
        eventId?.trim()
        && clanId?.trim()
        && boostId?.trim()
        && level !== '' && Number.isFinite(levelNumber) && levelNumber > 0
        && buyerUserId?.trim()
        && reason?.trim()
    );

    const handleSubmit = async () => {
        const body = {
            eventId: eventId.trim(),
            clanId: clanId.trim(),
            boostId: boostId.trim(),
            level: levelNumber,
            buyerUserId: buyerUserId.trim(),
            reason: reason.trim(),
        };
        const response = await addBoost(body);
        setConfirmOpen(false);
        if (response.hasOwnProperty('error')) {
            const message = response.error?.data?.message || response.error?.data || 'Failed to add boost';
            addAlert({text: typeof message === 'string' ? message : JSON.stringify(message), type: 'error'});
        } else {
            addAlert({text: `Boost ${boostId} (lvl ${levelNumber}) added to clan ${clanId}`, type: 'success'});
            setBoostId('');
            setLevel('1');
            setReason('');
        }
    };

    return (
        <Card>
            <CardContent>
                <Stack direction="row" spacing={1} sx={{alignItems: 'center', mb: 2}}>
                    <Typography variant="h6">Add Boost</Typography>
                </Stack>
                <Typography variant="body2" sx={{color: 'text.secondary', mb: 2}}>
                    Manually adds a boost to a clan within the event scope.
                    Skipped if the same boost (eventId + clanId + boostId + level) already exists.
                </Typography>

                {!eventId && (
                    <Alert severity="warning" sx={{mb: 2}}>
                        Select an event from the table above
                    </Alert>
                )}

                <Stack spacing={2}>
                    <TextField
                        label="Event ID" size="small" fullWidth required
                        value={eventId || ''}
                        InputProps={{readOnly: true}}
                        helperText="Set by clicking a row in the events table"
                    />
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth required
                        value={clanId}
                        onChange={(e) => { setClanId(e.target.value); setClanIdTouched(true); }}
                        helperText="Auto-filled from player's clan; override if needed"
                    />
                    <TextField
                        label="Boost ID" size="small" fullWidth required
                        value={boostId} onChange={(e) => setBoostId(e.target.value)}
                        helperText="Boost type identifier"
                    />
                    <TextField
                        label="Level" size="small" fullWidth required type="number"
                        value={level} onChange={(e) => setLevel(e.target.value)}
                        inputProps={{min: 1}}
                    />
                    <TextField
                        label="User ID" size="small" fullWidth required
                        value={buyerUserId}
                        onChange={(e) => { setBuyerUserId(e.target.value); setBuyerTouched(true); }}
                        helperText="Operator/user on whose behalf the boost is added (audit log)"
                    />
                    <TextField
                        label="Reason" size="small" fullWidth required multiline minRows={2}
                        value={reason} onChange={(e) => setReason(e.target.value)}
                        helperText="Goes to the audit log"
                    />
                </Stack>

                <Box sx={{mt: 2}}>
                    <LoadingButton
                        loading={addBoostResponse.isLoading}
                        variant="contained" color="primary"
                        startIcon={<BoltIcon/>}
                        disabled={!isValid}
                        onClick={() => setConfirmOpen(true)}
                    >
                        Add Boost
                    </LoadingButton>
                </Box>
            </CardContent>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogContent>
                    <Typography variant="h6" gutterBottom>Confirm Add Boost</Typography>
                    <Typography>Add boost <strong>{boostId}</strong> (level {levelNumber}) to clan</Typography>
                    <Typography sx={{}}>{clanId}</Typography>
                    <Typography>on event</Typography>
                    <Typography sx={{}}>{eventId}</Typography>
                    <Typography sx={{mt: 1}}>Reason: <em>{reason}</em></Typography>
                </DialogContent>
                <DialogActions>
                    <Button  onClick={() => setConfirmOpen(false)}>Cancel</Button>
                    <LoadingButton
                        loading={addBoostResponse.isLoading}
                        variant="contained" color="info"
                        onClick={handleSubmit}
                    >
                        Confirm
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Card>
    );
}


function AllianceBoostsCard({eventId, defaultClanId}) {
    const [clanId, setClanId] = useState(defaultClanId || '');
    const [clanIdTouched, setClanIdTouched] = useState(false);

    useEffect(() => {
        if (!clanIdTouched) setClanId(defaultClanId || '');
    }, [defaultClanId, clanIdTouched]);

    const trimmedClanId = clanId.trim();
    const skip = !eventId || !trimmedClanId;

    const {
        data: boosts,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetBossAllianceBoostsTOHQuery({eventId, clanId: trimmedClanId}, {skip});

    return (
        <Card>
            <CardContent>
                <Stack direction="row" sx={{alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                    <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                        <BoltIcon color="info"/>
                        <Typography variant="h6">Alliance Boosts</Typography>
                    </Stack>
                    {!skip && (
                        <Tooltip title="Reload boosts">
                            <span>
                                <IconButton size="small" onClick={refetch} disabled={isFetching}>
                                    <RefreshIcon/>
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start', mb: 2}}>
                    <TextField
                        label="Clan ID (UUID)" size="small" fullWidth
                        value={clanId}
                        onChange={(e) => { setClanId(e.target.value); setClanIdTouched(true); }}
                        helperText={
                            defaultClanId
                                ? "Auto-filled from current player's clan; override to view another clan"
                                : "Enter a clan ID to view its boosts"
                        }
                    />
                    {clanIdTouched && defaultClanId && (
                        <Tooltip title="Reset to player's clan">
                            <span>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => { setClanId(defaultClanId); setClanIdTouched(false); }}
                                >
                                    Reset
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </Stack>

                {!eventId ? (
                    <Alert severity="info">
                        Select an event above to view boosts
                    </Alert>
                ) : !trimmedClanId ? (
                    <Alert severity="info">
                        Enter a Clan ID to view boosts
                    </Alert>
                ) : (
                    <>
                        <Typography variant="caption" sx={{color: 'text.secondary', display: 'block', mb: 1}}>
                            Event <Box component="span" sx={{}}>{eventId}</Box>
                            {' · '}
                            Clan <Box component="span" sx={{}}>{trimmedClanId}</Box>
                        </Typography>

                        <Loading isLoading={isLoading} isError={isError} error={error}>
                            {Array.isArray(boosts) && boosts.length > 0 ? (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Boost ID</TableCell>
                                            <TableCell align="right">Level</TableCell>
                                            <TableCell>Bought by (User ID)</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {boosts.map((b, idx) => (
                                            <TableRow key={`${b.boostId}-${b.level}-${b.buyerUserId}-${idx}`}>
                                                <TableCell sx={{}}>
                                                    {b.boostId}
                                                </TableCell>
                                                <TableCell align="right">
                                                    <Chip label={b.level} size="small" color="info" variant="outlined"/>
                                                </TableCell>
                                                <TableCell sx={{}}>
                                                    {b.buyerUserId}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography sx={{color: 'text.secondary', py: 1}}>
                                    No boosts for this clan in this event
                                </Typography>
                            )}
                        </Loading>
                    </>
                )}
            </CardContent>
        </Card>
    );
}


const BOSS_RECALC_INVALIDATE_TAGS = [
    'TohBossLeagues',
    'TohBossLeagueTop',
    'TohBossAllianceProgress',
];


function BossEventsTOH({deviceId = '', defaultClanId = ''}) {
    const {
        data,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetAroundBossAllianceEventsTOHQuery();

    const events = flattenAroundEvents(data);
    const [selectedEventId, setSelectedEventId] = useState('');

    useEffect(() => {
        if (!selectedEventId && events.length > 0) {
            const active = events.find((e) => eventStatus(e).label === 'Active');
            setSelectedEventId((active || events[0]).id);
        }
    }, [events, selectedEventId]);

    return (
        <Box>
            <Box sx={{mb: 2}}>
                <ActiveEventsCard
                    events={events}
                    isLoading={isLoading}
                    isFetching={isFetching}
                    isError={isError}
                    error={error}
                    onRefresh={refetch}
                    onSelectEvent={setSelectedEventId}
                    selectedEventId={selectedEventId}
                    defaultClanId={defaultClanId}
                />
            </Box>

            <Grid container spacing={2} sx={{mb: 2}}>
                <Grid size={{xs: 12, md: 6}}>
                    <AllianceBoostsCard eventId={selectedEventId} defaultClanId={defaultClanId}/>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <ClanLeagueProgressCard
                        eventId={selectedEventId}
                        defaultClanId={defaultClanId}
                        pointsLabel="Total Damage"
                        useProgressQuery={useGetBossAllianceProgressTOHQuery}
                    />
                </Grid>
            </Grid>

            <Box sx={{mb: 2}}>
                <LeagueOverviewCard
                    eventId={selectedEventId}
                    pointsLabel="Total Damage"
                    useLeaguesQuery={useGetBossLeaguesTOHQuery}
                    useLeagueTopQuery={useGetBossLeagueTopTOHQuery}
                    useAwardMutation={useAwardBossLeagueTOHMutation}
                    useSetScoresMutation={useSetBossLeagueScoresTOHMutation}
                />
            </Box>

            <Grid container spacing={2}>
                <Grid size={{xs: 12, md: 6}}>
                    <AddDamageCard
                        eventId={selectedEventId}
                        deviceId={deviceId}
                        defaultClanId={defaultClanId}
                    />
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <AddBoostCard
                        eventId={selectedEventId}
                        deviceId={deviceId}
                        defaultClanId={defaultClanId}
                    />
                </Grid>
            </Grid>

            <Box sx={{mb: 2}}>
                <LeagueRecalculationCard
                    events={events}
                    defaultTargetEventId={selectedEventId}
                    useStatusQuery={useGetBossLeagueRecalculationStatusTOHQuery}
                    useScheduleMutation={useScheduleBossLeagueRecalculationTOHMutation}
                    invalidateTags={BOSS_RECALC_INVALIDATE_TAGS}
                />
            </Box>
        </Box>
    );
}


export default BossEventsTOH;
